import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronRight,
  Briefcase, 
  Check, 
  Coins, 
  Users, 
  Compass, 
  ArrowRight 
} from "lucide-react";
import { cn } from "@/lib/utils";
import logo from '../assets/images/logo-artie.png';
import { useProjectStore } from "../stores/projectStore";

export default function ProjectCreationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { draft, updateDraft, resetDraft } = useProjectStore();
  
  // Use local state for immediate feedback, sync with store
  const [selectedGoal, setSelectedGoalState] = useState<string>(draft.goal || "brand_partners");

  // Sync with store when local state changes
  useEffect(() => {
    updateDraft({ goal: selectedGoal });
  }, [selectedGoal, updateDraft]);

  // Handle draft reset logic on mount
  useEffect(() => {
    // If we have "keepDraft" in location state, we preserve the draft.
    // This happens when we navigate back from the details page (because we set it before leaving).
    // Otherwise (fresh visit from dashboard/link), we reset the draft to ensure a clean slate.
    if (location.state && (location.state as any).keepDraft) {
      console.log("Keeping draft state (returning user)");
    } else {
      console.log("Resetting draft state (new session)");
      resetDraft();
      // Also reset local state to default
      setSelectedGoalState("brand_partners");
    }
  }, [location.state, resetDraft]);

  const handleContinue = () => {
    // 1. Update the current history entry to have "keepDraft: true".
    // This ensures that if the user clicks the browser Back button from the next page,
    // they land back here with this state flag, preventing a reset.
    navigate('.', { replace: true, state: { keepDraft: true } });
    
    // 2. Navigate to the next page
    setTimeout(() => {
        navigate('/projects/new-details');
    }, 0);
  };

  const setSelectedGoal = (goal: string) => {
    setSelectedGoalState(goal);
    updateDraft({ goal });
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full pt-8 pr-10 pb-8 pl-10 relative">
      {/* Background Grid Animation */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:32px_32px]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent"></div>
      </div>

      <div className="max-w-5xl mx-auto w-full pt-6 relative z-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8 font-ibm">
          <Link to="/dashboard" className="hover:text-foreground transition-colors">
            Dashboard
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-foreground font-medium">
            New Project
          </span>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row text-center md:text-left max-w-3xl mx-auto mb-10 items-center justify-center gap-8">
          <div className="w-24 h-24 relative shrink-0">
            <div className="absolute inset-0 bg-secondary rounded-full blur-xl opacity-50"></div>
            <img 
              src={logo} 
              alt="Artie" 
              className="w-full h-full rounded-full object-cover border-4 border-background shadow-xl relative z-10" 
            />
            <div className="absolute -right-8 -top-3 rotate-6 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-xl shadow-lg z-20 border-2 border-background whitespace-nowrap">
              Choose wisely! 🐾
            </div>
          </div>
          <div className="flex flex-col">
            <h1 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet mb-3">
              What is your primary goal?
            </h1>
            <p className="text-muted-foreground font-ibm text-base max-w-lg">
              Select the main outcome you want to achieve with this project to
              help us tailor the analysis.
            </p>
          </div>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10 max-w-3xl mx-auto">
          {/* Option 1: Brand Partners */}
          <label 
            className={cn(
              "group relative flex flex-col text-left p-6 rounded-xl border-2 cursor-pointer transition-all shadow-sm hover:shadow-md h-full",
              selectedGoal === "brand_partners" 
                ? "border-primary bg-primary/5" 
                : "border-border bg-card opacity-70 grayscale-[0.3] hover:opacity-100 hover:grayscale-0"
            )}
            onClick={() => setSelectedGoal("brand_partners")}
          >
            <input 
              type="radio" 
              name="project_goal" 
              value="brand_partners" 
              className="peer sr-only" 
              checked={selectedGoal === "brand_partners"}
              onChange={() => setSelectedGoal("brand_partners")}
            />
            <div className="flex items-start justify-between mb-4">
              <div className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center shadow-sm",
                selectedGoal === "brand_partners" 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-secondary text-muted-foreground border border-border"
              )}>
                <Briefcase className="w-5 h-5" />
              </div>
              {selectedGoal === "brand_partners" && (
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                  <Check className="w-3.5 h-3.5 stroke-[3]" />
                </div>
              )}
            </div>
            <h3 className="text-lg font-semibold text-foreground font-cabinet mb-2 tracking-tight">
              Find Brand Partners
            </h3>
            <p className="text-sm text-muted-foreground font-ibm leading-relaxed">
              Match your script's themes and audience with brands looking for
              product placement or sponsorship.
            </p>
          </label>

          {/* Option 3: Collaborators */}
          <div className="relative flex flex-col text-left p-6 rounded-xl border border-border bg-card opacity-70 grayscale-[0.3] hover:opacity-100 hover:grayscale-0 transition-all group h-full">
            <div className="absolute top-4 right-4 z-10">
              <button className="text-[10px] font-semibold bg-secondary text-foreground border border-border px-2.5 py-1 rounded-md hover:bg-foreground hover:text-background transition-colors shadow-sm font-ibm">
                Notify me
              </button>
            </div>
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary text-muted-foreground flex items-center justify-center border border-border">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-foreground font-cabinet mb-2 tracking-tight">
              Find Collaborators
            </h3>
            <p className="text-sm text-muted-foreground font-ibm leading-relaxed">
              Discover producers, directors, or talent aligned with your
              project's vision.
            </p>
          </div>

          {/* Option 2: Equity Investors */}
          <div 
            className="relative flex flex-col text-left p-6 rounded-xl border border-border bg-card opacity-70 grayscale-[0.3] hover:opacity-100 hover:grayscale-0 transition-all group h-full"
            // Note: This option is disabled in the prototype (no input), but we can make it selectable if needed later. 
            // For now, mirroring the "Disabled" state visually but adding "Notify me" button behavior if needed.
          >
            <div className="absolute top-4 right-4 z-10">
              <button className="text-[10px] font-semibold bg-secondary text-foreground border border-border px-2.5 py-1 rounded-md hover:bg-foreground hover:text-background transition-colors shadow-sm font-ibm">
                Notify me
              </button>
            </div>
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary text-muted-foreground flex items-center justify-center border border-border">
                <Coins className="w-5 h-5" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-foreground font-cabinet mb-2 tracking-tight">
              Find Investors
            </h3>
            <p className="text-sm text-muted-foreground font-ibm leading-relaxed">
              Connect with angels, VCs, and film funds looking to finance
              independent productions.
            </p>
          </div>

          {/* Option 4: Just Exploring */}
          <div className="relative flex flex-col text-left p-6 rounded-xl border border-border bg-card opacity-70 grayscale-[0.3] hover:opacity-100 hover:grayscale-0 transition-all group h-full">
            <div className="absolute top-4 right-4 z-10">
              <button className="text-[10px] font-semibold bg-secondary text-foreground border border-border px-2.5 py-1 rounded-md hover:bg-foreground hover:text-background transition-colors shadow-sm font-ibm">
                Notify me
              </button>
            </div>
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-secondary text-muted-foreground flex items-center justify-center border border-border">
                <Compass className="w-5 h-5" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-foreground font-cabinet mb-2 tracking-tight">
              Just Exploring
            </h3>
            <p className="text-sm text-muted-foreground font-ibm leading-relaxed">
              Analyze my script structure and marketability without specific
              partnership goals.
            </p>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-center pt-2">
          <button
          className="px-8 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_4px_14px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.15)] flex items-center gap-2 font-ibm"
          onClick={handleContinue}
        >
            <span>Continue to Script Upload</span>
            <ArrowRight className="w-4 h-4 stroke-[2]" />
          </button>
        </div>
      </div>
    </div>
  );
}